import React from 'react';
import { useSelector } from 'react-redux';
import {
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Typography,
    IconButton,
    Switch,
    Divider,
    Tooltip,
    Slider
} from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DeleteIcon from '@material-ui/icons/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { HexColorPicker } from "react-colorful";
import { Box } from '@mui/material';

export default function MaskEditor(props) {
    const [maskInfoOpen, setMaskInfoOpen] = React.useState(false);
    const [currMask, setCurrMask] = React.useState([]);
    const ICON_SIZE = 24;

    const handleInfoOpen = (index) => {
        const mask = props.masks[index];
        if (!mask) return;
        setCurrMask({
            ...mask,
            points: Array.isArray(mask.points)
                ? mask.points.map(point => ({ ...point }))
                : mask.points
        });
        setMaskInfoOpen(true);
    };

    const handleChangeMask = (e) => {
        const { name, value } = e.target;
        setCurrMask(prev => ({ ...prev, [name]: value }));
    };
    const handleChangePosition = (e) => {
        const { name, value } = e.target;
        const parsed = Number(value);
        setCurrMask(prev => ({
            ...prev,
            [name]: Number.isFinite(parsed) ? parsed : prev?.[name] ?? 0
        }));
    };

    const handleChangeOpacity = (event, newValue) => {
        // console.log("Opacity", newValue);
        setCurrMask((prev) => ({ ...prev, opacity: newValue }));
    };

    const toggleNameVisibility = () => {
        setCurrMask((prevMask) => ({
            ...prevMask,
            visible: !prevMask.visible,
        }));
    };

    const toggleMaskIn = () => {
        setCurrMask((prevMask) => ({
            ...prevMask,
            maskIn: !prevMask.maskIn,
        }));
    };

    const handleColor = (e) => {
        setCurrMask(prev => ({ ...prev, color: e }));
    };

    const handleSave = () => {
        if (currMask) {
            props.updateMask(currMask);
        }
        console.log("Save", currMask);
        setMaskInfoOpen(false);
    };

    const handleOnDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;
        if (destination.index === source.index) return;

        const reorderedMasks = Array.from(props.masks);
        const [removed] = reorderedMasks.splice(source.index, 1);
        reorderedMasks.splice(destination.index, 0, removed);

        reorderedMasks.forEach((mask, index) => {
            mask.layer = index;
        });

        props.updateMaskOrder(reorderedMasks);
    };

    return (
        <>
            <Dialog open={maskInfoOpen} onClose={() => setMaskInfoOpen(false)}>
                <DialogTitle>Mask Setting</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Mask Name"
                        value={currMask?.name}
                        onChange={handleChangeMask}
                        variant="outlined"
                        name="name"
                        style={{ margin: "10px" }}
                    />
                    {currMask?.type !== "polygon" && (
                        <Box display="flex" gap={1} style={{ margin: "10px" }}>
                            <TextField
                                label="X"
                                name="x"
                                type="number"
                                value={Number(currMask?.x).toFixed(2) ?? 0}
                                onChange={handleChangePosition}
                                variant="outlined"
                                size="small"
                            />
                            <TextField
                                label="Y"
                                name="y"
                                type="number"
                                value={Number(currMask?.y).toFixed(2) ?? 0}
                                onChange={handleChangePosition}
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography>Show Name</Typography>
                        <Switch
                            onChange={toggleNameVisibility}
                            checked={!currMask?.visible}
                            name="showName"
                            color="primary"
                        />

                        <Typography>Masking In?</Typography>
                        <Switch
                            onChange={toggleMaskIn}
                            checked={currMask?.maskIn}
                            name="maskingIn"
                            color="primary"
                        />
                    </Box>
                    <TextField
                        label="Opacity"
                        value={currMask?.opacity}
                        variant="outlined"
                        name="opacity"
                        style={{ margin: "10px", display: "none" }}
                    />
                    <Typography gutterBottom>Opacity</Typography>
                    <Slider
                        value={currMask?.opacity || 0.5} // Default to 0.5 if undefined
                        onChange={handleChangeOpacity}
                        step={0.1}
                        min={0.1}
                        max={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{
                            margin: "10px",
                            width: "90%",
                            color: "#1976d2",
                            '& .MuiSlider-thumb': {
                                width: 20,
                                height: 20,
                            },
                            '& .MuiSlider-rail': {
                                height: 8,
                            },
                            '& .MuiSlider-track': {
                                height: 8,
                            },
                        }}
                    />

                    <HexColorPicker color={currMask?.color || "#ADD8E6"} onChange={handleColor} />
                    Selected Color: {currMask?.color || "#ADD8E6"}

                </DialogContent>
                <div style={{ textAlign: "right", margin: 10 }}>
                    <Button
                        onClick={handleSave}
                        color={"primary"}
                        style={{ margin: "10px" }}
                        variant="contained"
                    >
                        Save
                    </Button>
                    <Button
                        onClick={() => setMaskInfoOpen(false)}
                        color={"primary"}
                        style={{ margin: "10px" }}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </div>
            </Dialog>

            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="droppable-list">
                    {(provided) => (

                        <Box sx={{ maxHeight: "500px", overflowY: "auto", width: "100%" }}>
                            <List ref={provided.innerRef} {...provided.droppableProps}
                                style={{
                                    background: "#f9f9f9",
                                    padding: "10px",
                                    borderRadius: "8px",
                                }}
                            >
                                {props.masks
                                    .sort((a, b) => a.layer - b.layer)
                                    .map((mask, index) => (
                                        <Draggable key={index} draggableId={String(index)} index={index}>
                                            {(provided, snapshot) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        background: snapshot.isDragging
                                                            ? "rgba(0, 123, 255, 0.1)"
                                                            : "#fff",
                                                        border: mask.selected
                                                            ? "2px solid darkturquoise"
                                                            : "1px solid #ccc",
                                                        marginBottom: "8px",
                                                        borderRadius: "4px",
                                                        boxShadow: snapshot.isDragging
                                                            ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                                                            : "none",
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                    onClick={() => props.selectMask(mask.id)}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Tooltip title={mask.name}>
                                                                <Typography
                                                                    noWrap
                                                                    sx={{
                                                                        maxWidth: "150px",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        display: "block"
                                                                    }}
                                                                >
                                                                    {mask.name.length > 15 ? mask.name.slice(0, 14) + "…" : mask.name}
                                                                </Typography>
                                                            </Tooltip>
                                                        }
                                                        secondary={<>
                                                            Type: {mask.type}
                                                            <br />
                                                            {mask.maskIn ? "Masking in" : "Masking out"}
                                                        </>}
                                                        style={{ userSelect: 'none' }}
                                                    />
                                                    <ListItemSecondaryAction style={{ display: "flex", alignItems: "center" }}>
                                                        <IconButton
                                                            onClick={() => handleInfoOpen(index)}
                                                            style={{ padding: 4 }}
                                                        >
                                                            <SettingsIcon style={{ fontSize: ICON_SIZE }} />
                                                        </IconButton>
                                                        <Tooltip title={mask.locked ? "Unlock Mask" : "Lock Mask"} placement="top">
                                                            <IconButton
                                                                onClick={() => props.lockMask(mask.id)}
                                                                style={{ padding: 4 }}
                                                            >
                                                                {mask.locked
                                                                    ? <LockIcon style={{ fontSize: ICON_SIZE }} />
                                                                    : <LockOpenIcon style={{ fontSize: ICON_SIZE }} />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Mask" placement="top">
                                                            <IconButton
                                                                onClick={() => props.deleteMask(mask.id)}
                                                                style={{ padding: 4 }}
                                                            >
                                                                <DeleteIcon style={{ fontSize: ICON_SIZE }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={props.masks.length !== props.maskList.length ? "Turn off filter to reorder" : ""}
                                                            placement="top"
                                                        >
                                                            <span>
                                                                <Tooltip title="Drag to Reorder" placement="top">
                                                                    <IconButton
                                                                        {...provided.dragHandleProps}
                                                                        style={{ cursor: "grab", padding: 4 }}
                                                                        disabled={props.masks.length !== props.maskList.length || props.masks.length === 1}
                                                                    >
                                                                        <DragIndicatorIcon style={{ fontSize: ICON_SIZE }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </span>
                                                        </Tooltip>


                                                    </ListItemSecondaryAction>

                                                </ListItem>
                                            )}
                                        </Draggable>
                                    ))}
                                {provided.placeholder}
                            </List>
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
}
